import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatDateRangeUTC } from "@/lib/format";
import { ScreenSpinner } from "@/components/ui/Spinner";
import type { Tournament } from "@/types";

// Réplica de reference/expo-frontend/app/proximos-torneios.tsx: lista de
// torneios "upcoming" fora da Home, cada um levando à tela de palpite.
export default function ProximosTorneiosPage() {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<Tournament[]>("/api/tournaments", { auth: false })
      .then(setTournaments)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upcoming = useMemo(
    () =>
      tournaments
        .filter((t) => t.status === "upcoming")
        .sort(
          (a, b) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
        ),
    [tournaments],
  );

  if (loading) return <ScreenSpinner />;

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <div className="flex items-center gap-3">
        <Link
          to="/"
          aria-label="Voltar"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="text-xl font-bold">Próximos torneios</h1>
          <p className="text-[13px] text-muted-foreground">
            Escolha um evento para apostar
          </p>
        </div>
      </div>

      {upcoming.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum torneio programado no momento.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {upcoming.map((t) => (
            <Link
              key={t.id}
              to={`/palpite/${t.id}`}
              className="flex items-center justify-between rounded-(--radius-field) border border-border bg-card px-4 py-3 transition-colors hover:border-tint/40"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{t.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {t.course}
                </p>
              </div>
              <span className="ml-3 flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                {formatDateRangeUTC(t.startDate, t.endDate)}
                <ChevronRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
